/**
 * @file useWriteoffDocument.ts
 * @description Данные карточки списания: загрузка и операции со строками
 * (phase19.23, A2).
 *
 * Вынесено из страницы (SRP): странице остаётся раскладка документа, хуку —
 * запросы. Список строк после мутаций перечитываем с сервера, а не правим
 * руками — один источник правды.
 */

import { useCallback, useEffect, useState } from "react";

import { useNotify } from "../ui/NotifyProvider";
import type { WriteoffItemPayload } from "./WriteoffItemForm";
import { writeoffsApi, type WriteoffDto } from "../../lib/api-wms";
import { wmsErrorText } from "../../lib/wms-error-text";

export function useWriteoffDocument(id: string | undefined, isNew: boolean) {
  const notify = useNotify();
  const [writeoff, setWriteoff] = useState<WriteoffDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (isNew || !id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    writeoffsApi
      .findOne(id)
      .then((data) => {
        if (cancelled) return;
        setWriteoff(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить списание"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  /**
   * Перечитать документ с сервера — один источник правды.
   *
   * Нужен и после проведения: ответ `POST /commit` приходит без `items` и
   * `createdBy`, поэтому подменять им state нельзя (шапка показывала бы
   * «Позиции (0)» до F5).
   */
  const reload = useCallback(async (writeoffId: string) => {
    setWriteoff(await writeoffsApi.findOne(writeoffId));
  }, []);

  const addItem = useCallback(
    async (payload: WriteoffItemPayload): Promise<boolean> => {
      if (!writeoff) return false;
      setAdding(true);
      try {
        await writeoffsApi.addItem(writeoff.id, payload);
        await reload(writeoff.id);
        return true;
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось добавить позицию"));
        return false;
      } finally {
        setAdding(false);
      }
    },
    [writeoff, notify, reload],
  );

  const removeItem = useCallback(
    async (itemId: string) => {
      if (!writeoff) return;
      try {
        await writeoffsApi.removeItem(writeoff.id, itemId);
        await reload(writeoff.id);
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось удалить позицию"));
      }
    },
    [writeoff, notify, reload],
  );

  return { writeoff, loading, error, adding, reload, addItem, removeItem };
}
